import { ReactElement, useEffect } from 'react'
import { useQuery, gql, NetworkStatus } from '@apollo/client'
import { defineMessages, useIntl, FormattedMessage } from 'react-intl'
import { NavLink, useLocation, useParams } from 'react-router-dom'
import { isEmpty } from 'lodash'

import EmptyState from 'components/v2/EmptyState'
import { Loader } from '@10xLabs/react-web/components/Loader'
import { Button } from 'components/v2/Button'
import { ContentBody } from 'components/v2/Content'
import { ForbiddenView } from 'features/App/components/ForbiddenView'
import { withLastUrl } from 'features/App/components/WithLastUrl'
import { InventoryActions } from 'features/Inventories/components/InventoryActions'
import { InventoryHeader } from 'features/Inventories/components/InventoryHeader'
import { InventoryTabs } from 'features/Inventories/components/InventoryTabs'
import { InventoryContent } from 'features/Inventories/components/InventoryContent'
import { TYPE } from 'features/Inventories/components/Type/constants'
import { FEATURES } from 'features/Roles/components/Permissions/constants'
import { useSanitizeItem } from 'resolvers/items'
import { filterEmptyValue } from 'utils/object'
import { getQueries } from 'utils/route'
import { canWriteBase, canWriteWarehouse, canReadInventory } from 'features/Users/utils'
import { InboundsFilters } from './Filters'
import { InboundsList } from './List'
import { InboundsPagination } from './Pagination'
import { useLogin } from 'features/Login/hooks'

const LIMIT = 30

const messages = defineMessages({
  helmet: {
    id: 'inventory.inbounds.helmet',
    defaultMessage: 'Entradas | Nexbus Admin'
  }
})

const GET_INVENTORY = gql`
  query GetInventory($inventoryId: ID!) {
    inventory(input: { id: $inventoryId }) {
      id
      name
      type
      company {
        id
        name
      }
    }
  }
`

const GET_INBOUND_ORDERS = gql`
  query GetInboundOrders(
    $limit: Int!
    $page: Int!
    $destinationId: ID
    $id: ID
    $conciliationStatus: OrderConciliationStatus
    $itemId: ID
  ) {
    orders(
      pagination: { size: $limit, page: $page }
      input: { destinationID: $destinationId, id: $id, conciliationStatus: $conciliationStatus, itemID: $itemId }
    ) {
      total
      items {
        id
        status
        conciliationStatus
        createdAt
        type
        origin {
          id
          name
        }
        supplier
        createdBy {
          id
          username
          firstName
          lastName
        }
        lineItems {
          itemID
          name
          container
          netContent
          units
          ordered
          received
        }
      }
    }
  }
`

const InventoryInboundsView = (): ReactElement => {
  let content
  const location = useLocation()
  const { inventoryId } = useParams()
  const intl = useIntl()
  const login = useLogin()

  const hasReadPermission = canReadInventory(login.accessToken.policies)

  useEffect(() => {
    document.title = intl.formatMessage(messages.helmet)
  }, [])

  const { data: inventoryData } = useQuery(GET_INVENTORY, {
    variables: {
      inventoryId
    },
    skip: !hasReadPermission
  })

  // eslint-disable-next-line prefer-const
  let { page = 1, id = '', conciliationStatus = '', itemId = '' } = getQueries(location.search)
  // @ts-expect-error TS(2559) FIXME Remove this comment to see the full error message
  page = parseInt(page)
  const { companyId, policies } = login.accessToken
  const sanitizedItem = useSanitizeItem(itemId, companyId)

  const variables = filterEmptyValue({
    page: page - 1,
    limit: LIMIT,
    destinationId: inventoryId,
    id,
    conciliationStatus,
    itemId
  })

  const {
    loading,
    error,
    data = {},
    refetch,
    networkStatus
  } = useQuery(GET_INBOUND_ORDERS, {
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
    notifyOnNetworkStatusChange: true,
    variables,
    skip: !hasReadPermission
  })

  useEffect((): TSFixMe => {
    if (hasReadPermission) {
      refetch()
    }
  }, [])

  if (!hasReadPermission) {
    return <ForbiddenView feature={FEATURES.INVENTORY} />
  }

  const filters = {
    id,
    conciliationStatus,
    item: sanitizedItem
  }

  if (networkStatus === NetworkStatus.refetch || loading) {
    content = (
      <EmptyState.Container>
        <Loader />
      </EmptyState.Container>
    )
  } else if (error) {
    content = (
      <EmptyState.Container>
        <EmptyState.Description>
          <FormattedMessage
            id="spanish.inventory.inbounds.error.description"
            defaultMessage="Hubo un error al cargar tus entradas"
          />
        </EmptyState.Description>

        <EmptyState.Action>
          <Button onClick={(): TSFixMe => refetch()} kind="primary">
            <FormattedMessage id="spanish.inventory.inbounds.error.action" defaultMessage="Recargar las entradas" />
          </Button>
        </EmptyState.Action>
      </EmptyState.Container>
    )
  } else if (isEmpty(data.orders.items) && page === 1 && isEmpty(filterEmptyValue(filters))) {
    content = (
      <EmptyState.Container>
        <EmptyState.Description>
          <FormattedMessage
            id="spanish.inventory.inbounds.empty.description"
            defaultMessage="No tienes entrada todavía"
          />
        </EmptyState.Description>

        <EmptyState.Action>
          <Button as={NavLink} to={`/inventories/${inventoryId}/receive-bulk-items`} kind="primary">
            <FormattedMessage id="spanish.inventory.inbounds.empty.action" defaultMessage="Agregar una entrada" />
          </Button>
        </EmptyState.Action>
      </EmptyState.Container>
    )
  } else if (isEmpty(data.orders.items)) {
    content = (
      <EmptyState.Container>
        <EmptyState.Description>
          <FormattedMessage
            id="spanish.inventory.inbounds.empty.filters.description"
            defaultMessage="No se encontró entradas"
          />
        </EmptyState.Description>

        <EmptyState.Action>
          <Button as={NavLink} to={`/inventories/${inventoryId}/inbounds`} kind="primary">
            <FormattedMessage id="spanish.inventory.inbounds.empty.filters.action" defaultMessage="Borrar filtros" />
          </Button>
        </EmptyState.Action>
      </EmptyState.Container>
    )
  } else {
    content = (
      <>
        <InboundsList orders={data.orders.items} inventory={inventoryData && inventoryData.inventory} />

        <InboundsPagination
          page={page}
          inventoryId={inventoryId}
          limit={LIMIT}
          totalItems={data.orders.total}
          currentItemsCount={data.orders.items.length}
          filters={filters}
        />
      </>
    )
  }

  const inventory = (inventoryData && inventoryData.inventory) || {}
  const canWrite =
    (inventory.type === TYPE.BASE && canWriteBase(policies)) ||
    (inventory.type === TYPE.WAREHOUSE && canWriteWarehouse(policies))

  return (
    <>
      <InventoryHeader inventory={inventoryData && inventoryData.inventory} />
      <InventoryTabs inventoryId={inventoryId} />
      <InventoryContent>
        {canWrite && (
          <InventoryActions>
            <Button as={NavLink} to={`/inventories/${inventoryId}/receive-bulk-items`} kind="primary">
              <FormattedMessage id="spanish.inventory.inbounds.actions.create" defaultMessage="Crear" />
            </Button>
          </InventoryActions>
        )}
        <ContentBody>
          <InboundsFilters filters={filters} inventoryId={inventoryId} companyId={companyId} />
          <section>{content}</section>
        </ContentBody>
      </InventoryContent>
    </>
  )
}

export default withLastUrl(InventoryInboundsView)
